/**
 * Simulation Service — Shared account types and API fetching.
 *
 * Replaces hardcoded SIMULATION_ACCOUNTS with backend-driven data.
 * All shared accounts are READ ONLY — no execute, modify, or delete.
 */

// ═══════════════════════════════════════════════════════════════════
//  TYPES
// ═══════════════════════════════════════════════════════════════════

/**
 * Forex-specific payload, present only on OANDA paper accounts.
 * Existing Alpaca account code paths ignore this object.
 *
 * Phase 11 invariants surfaced through this object:
 *   - live_trading_status / live_limited_status / live_full_status are ALL
 *     'BLOCKED' for any forex paper account. The UI must NEVER offer a
 *     control that flips them.
 *   - environment is always 'practice'.
 *   - oanda_configured is the ONLY token-related field exposed; the actual
 *     broker secret is never returned by the backend.
 */
export interface ForexAccountPayload {
  broker: 'OANDA';
  environment: 'practice';
  system_state: 'SAFE_MODE' | 'READ_ONLY' | 'PAPER_TRADING';
  currency: string;
  leverage: number;
  margin_used: number;
  margin_available: number;
  open_positions_count: number;
  oanda_configured: boolean;
  live_trading_status: 'BLOCKED';
  live_limited_status: 'BLOCKED';
  live_full_status: 'BLOCKED';
  default_symbols: string[];
}

export interface SimulationAccount {
  id: string;
  name: string;
  strategy_type: string;
  style: string;

  // ── Alpaca-derived (authoritative) ──
  // currentBalance, realTradeCount, realStartDate, realRoi, realWinRate,
  // lastTradeTime, brokerConnected, lastVerifiedAt are the ONLY fields the
  // UI should render metrics from. Everything else exists for layout or
  // descriptive text and does not represent performance data.
  currentBalance: number | null;      // maps from current_balance (Alpaca equity)
  firstEquity: number | null;         // maps from first_equity (pinned on first healthy sync)
  dailyStartEquity: number | null;    // maps from daily_start_equity (Alpaca last_equity — yesterday's close = today's start)
  realRoi: number | null;             // maps from real_roi
  realWinRate: number | null;         // maps from real_win_rate
  realTradeCount: number | null;      // maps from real_trade_count
  realStartDate: string | null;       // maps from real_start_date (ISO)
  lastTradeTime: string | null;       // maps from last_trade_time
  brokerConnected: boolean;           // maps from broker_connected
  brokerStatus: string | null;        // maps from broker_status
  brokerEquity: number | null;        // maps from broker_equity
  brokerBuyingPower: number | null;   // maps from broker_buying_power
  lastVerifiedAt: string | null;      // maps from last_verified_at
  /** True iff broker is connected AND we have enough data to render metrics. */
  hasLiveData: boolean;

  // ── Configured identity (safe to render) ──
  // The canonical starting balance configured for this account when it was
  // first funded. Unlike first_equity (pinned on first sync), this is
  // user-defined truth and does NOT depend on when the Alpaca sync started.
  startingBalance: number | null;     // maps from starting_balance

  // ── Structural / descriptive (safe to render as-is) ──
  equityHistory: EquityPoint[];  // maps from equity_curve
  positions: Position[];
  aiStrategySummary: AIStrategySummary;
  reasoningCorrelation: ReasoningCorrelation[];
  readOnly: boolean;
  strategyName: string;
  strategyDescription: string;
  confidenceScore: number;
  dataQuality: string;
  daysOfHistory: number;
  accountType: string;
  managedBy: string;
  lastSyncedAt: string | null;

  /**
   * Phase 11: present only when accountType === 'forex_paper'. Existing UI
   * paths can ignore this. The Simulation Lab dispatches on its presence
   * to render the OANDA forex panel and the LIVE_TRADING_BLOCKED banner.
   */
  forex?: ForexAccountPayload;

  // ── Legacy (kept for backwards-compat but MUST NOT drive UI metrics) ──
  /** @deprecated Use currentBalance. */
  balance: number | null;
  /** @deprecated start_balance/starting_balance was hardcoded. */
  initialBalance: number | null;
  /** @deprecated roi was hardcoded; use realRoi. */
  roi: number | null;
  /** @deprecated win_rate was hardcoded; use realWinRate. */
  winRate: number | null;
  /** @deprecated drawdown is currently derived from equity_curve. */
  maxDrawdown: number | null;
  /** @deprecated trade_count was hardcoded; use realTradeCount. */
  tradeCount: number | null;
  /** @deprecated start_date was hardcoded; use realStartDate. */
  startDate: string | null;
  /** @deprecated active_duration was hardcoded. */
  activeDuration: string;
  /** @deprecated consistency_score was hardcoded. */
  consistencyScore: number;
  /** @deprecated pnl was derived from hardcoded starting_balance. */
  pnl: number | null;
}

export interface EquityPoint {
  time: string;
  value: number;
  drawdown: number;
}

export interface Position {
  symbol: string;
  side: string;
  qty: number;
  entry: number;
  current: number;
  pnl: number;
}

export interface AIStrategySummary {
  behavior: string;
  winConditions: string[];
  failurePatterns: string[];
}

export interface ReasoningCorrelation {
  pattern: string;
  winRate: number;
  roi: number;
}

export interface TradeLog {
  id: string;
  asset: string;
  action: string;
  type: 'ENTRY' | 'EXIT';
  time: string;
  confidence: number;
  agreement: number;
  pnl: number | null;
  oneLineReasoning: string;
  context: string;
  deliberation: {
    claude: string[];
    gpt: string[];
    gemini: string[];
    perplexity: string[];
  };
  signals: string[];
  outcome: {
    success: boolean;
    pnl: number;
    failure: string | null;
  };
  lessons: string[];
  accountId: string;
}

// ═══════════════════════════════════════════════════════════════════
//  NORMALIZER — guarantees every field React touches is safe
// ═══════════════════════════════════════════════════════════════════

function toArray(val: unknown): string[] {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string' && val) return [val];
  return ['—'];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normalizeTradeLog(trade: any): TradeLog {
  return {
    id:               trade.id || crypto.randomUUID(),
    time:             trade.time || trade.timestamp || '—',
    asset:            trade.asset || trade.symbol || 'UNKNOWN',
    action:           trade.action || trade.side || '—',
    type:             trade.type || 'ENTRY',
    confidence:       trade.confidence ?? 0,
    agreement:        trade.agreement ?? 0,
    pnl:              trade.pnl ?? 0,
    oneLineReasoning: trade.oneLineReasoning || trade.reasoning || '—',
    context:          trade.context || '—',
    deliberation: {
      claude:    toArray(trade.deliberation?.claude),
      gpt:       toArray(trade.deliberation?.gpt),
      gemini:    toArray(trade.deliberation?.gemini),
      perplexity: toArray(trade.deliberation?.perplexity),
    },
    signals:  Array.isArray(trade.signals) ? trade.signals : [],
    outcome: {
      success: trade.outcome?.success ?? ((trade.pnl ?? 0) >= 0),
      pnl:     trade.outcome?.pnl ?? trade.pnl ?? 0,
      failure: trade.outcome?.failure ?? null,
    },
    lessons:   Array.isArray(trade.lessons) ? trade.lessons : [],
    accountId: trade.accountId || '',
  };
}

// ═══════════════════════════════════════════════════════════════════
//  API
// ═══════════════════════════════════════════════════════════════════

// API calls use same-origin path /api/v1 which nginx proxies to backend port 8100
// This avoids exposing port 8100 publicly and prevents CORS issues
const BASE_URL = '/api/v1';

/** Numeric coercion that preserves null/undefined as null (so UI can hard-fail). */
function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}

function strOrNull(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null;
  return String(v);
}

function cardAgeDaysFrom(dateStr: string | null): number {
  if (!dateStr) return 0;
  const parsed = Date.parse(dateStr);
  if (!Number.isFinite(parsed)) return 0;
  return Math.floor((Date.now() - parsed) / 86400000);
}

function mapBackendAccount(raw: Record<string, unknown>): SimulationAccount {
  const brokerConnected = Boolean(raw.broker_connected);
  const currentBalance = numOrNull(raw.current_balance);
  const realTradeCount = numOrNull(raw.real_trade_count);
  const realRoi = numOrNull(raw.real_roi);
  const realStartDate = strOrNull(raw.real_start_date);
  const configuredStartDate = strOrNull(raw.start_date);
  const cardStartDate = configuredStartDate ?? realStartDate;
  const cardAgeDays = cardAgeDaysFrom(cardStartDate);

  // "Has live data" = broker is connected AND we have an equity number.
  // trade_count / roi can legitimately be 0 / null for brand-new accounts;
  // those cases are handled per-widget in the UI.
  const hasLiveData = brokerConnected && currentBalance !== null;

  return {
    id: raw.id as string,
    name: raw.name as string,
    strategy_type: raw.strategy_type as string,
    style: raw.style as string,

    // ── Alpaca-derived ──
    currentBalance,
    firstEquity:        numOrNull(raw.first_equity),
    dailyStartEquity:   numOrNull(raw.daily_start_equity),
    realRoi,
    realWinRate:        numOrNull(raw.real_win_rate),
    realTradeCount,
    realStartDate,
    lastTradeTime:      strOrNull(raw.last_trade_time),
    brokerConnected,
    brokerStatus:       strOrNull(raw.broker_status),
    brokerEquity:       numOrNull(raw.broker_equity),
    brokerBuyingPower:  numOrNull(raw.broker_buying_power),
    lastVerifiedAt:     strOrNull(raw.last_verified_at),
    hasLiveData,

    // ── Configured identity ──
    startingBalance:    numOrNull(raw.starting_balance),

    // ── Structural ──
    equityHistory:        ((raw.equity_curve as EquityPoint[]) || []).filter(p => {
      const sd = raw.start_date as string | undefined;
      if (!sd || !p.time) return true;
      return p.time >= sd;
    }),
    positions:            (raw.positions as Position[]) || [],
    aiStrategySummary:    raw.ai_strategy_summary as AIStrategySummary,
    reasoningCorrelation: (raw.reasoning_correlation as ReasoningCorrelation[]) || [],
    readOnly: raw.read_only as boolean,
    strategyName:         (raw.strategy_name as string) || '',
    strategyDescription:  (raw.strategy_description as string) || '',
    confidenceScore:      (raw.confidence_score as number) || 0,
    dataQuality:          (raw.data_quality as string) || 'high',
    daysOfHistory:        cardAgeDays,
    accountType:          (raw.account_type as string) || 'shared',
    managedBy:            (raw.managed_by as string) || 'council',
    lastSyncedAt:         strOrNull(raw.last_synced_at),
    forex:                (raw.forex as ForexAccountPayload | undefined),

    // ── Legacy aliases (do NOT use for display) ──
    balance:           currentBalance,
    initialBalance:    numOrNull(raw.starting_balance),
    roi:               realRoi,
    winRate:           numOrNull(raw.real_win_rate),
    maxDrawdown:       numOrNull(raw.drawdown),
    tradeCount:        realTradeCount,
    startDate:         cardStartDate,
    activeDuration:    `${cardAgeDays} day${cardAgeDays === 1 ? '' : 's'}`,
    consistencyScore:  (raw.consistency_score as number) || 0,
    pnl: currentBalance !== null && numOrNull(raw.first_equity) !== null
      ? currentBalance - (numOrNull(raw.first_equity) as number)
      : null,
  };
}

export async function fetchSharedAccounts(): Promise<SimulationAccount[]> {
  const res = await fetch(`${BASE_URL}/simulation/shared-accounts`, {
    headers: {
      'X-API-Key': localStorage.getItem('auth_token') || 'dev-admin-key',
    },
  });
  if (!res.ok) throw new Error(`Failed to fetch shared accounts: ${res.status}`);
  const json = await res.json();
  if (!json.ok) throw new Error(json.error?.message || 'API error');
  return (json.data.accounts as Record<string, unknown>[]).map((raw) => {
    const account = mapBackendAccount(raw);
    // Normalize trade_logs if the backend ever sends them
    const rawLogs = (raw as Record<string, unknown>).trade_logs;
    if (Array.isArray(rawLogs)) {
      (account as SimulationAccount & { trade_logs: TradeLog[] }).trade_logs =
        rawLogs.map(normalizeTradeLog);
    }
    return account;
  });
}

// ═══════════════════════════════════════════════════════════════════
//  MOCK TRADE LOGS (shared account trade history — read only)
// ═══════════════════════════════════════════════════════════════════

export const MOCK_TRADE_LOGS: TradeLog[] = ([
  {
    id: 'T001', asset: 'NVDA', action: 'BUY', type: 'ENTRY', time: '2026-03-15 14:30', confidence: 87, agreement: 82, pnl: 1240, accountId: 'sim-owl-momentum',
    oneLineReasoning: 'Momentum breakout above 50 EMA with volume surge',
    context: 'NVDA breaking out of 3-week consolidation with 2.5x average volume. Sector momentum aligned.',
    deliberation: { claude: ['Strong breakout with volume confirmation', 'Entry above key resistance'], gpt: ['Momentum signal confirmed by EMA crossover', 'Relative strength above 70'], gemini: ['Volume surge validates breakout', 'Risk-reward favorable at current levels'], perplexity: ['Historical pattern shows 72% follow-through', 'Similar setups averaged 3.2% gain'] },
    signals: ['EMA Crossover', 'Volume Surge', 'Sector Momentum', 'Relative Strength'],
    outcome: { success: true, pnl: 1240, failure: null },
    lessons: ['Volume confirmation remains the strongest momentum signal', 'Sector alignment increases win probability by 15%'],
  },
  {
    id: 'T002', asset: 'AAPL', action: 'BUY', type: 'ENTRY', time: '2026-03-10 10:15', confidence: 75, agreement: 78, pnl: 680, accountId: 'sim-elephant-value',
    oneLineReasoning: 'Value entry below sector average P/E',
    context: 'AAPL trading at 22x forward P/E vs sector 28x. Strong FCF generation.',
    deliberation: { claude: ['Undervalued relative to peers', 'Strong cash position provides safety'], gpt: ['P/E discount justified entry', 'Dividend yield provides downside cushion'], gemini: ['Fundamental value gap detected', 'Mean reversion likely within 6 months'], perplexity: ['Similar valuation entries have 68% success rate', 'Median holding period 4.2 months'] },
    signals: ['P/E Discount', 'Strong FCF', 'Dividend Support', 'Sector Rotation'],
    outcome: { success: true, pnl: 680, failure: null },
    lessons: ['Patience in value entries yields better risk-adjusted returns', 'FCF quality matters more than growth rate for value plays'],
  },
  {
    id: 'T003', asset: 'TSLA', action: 'BUY', type: 'ENTRY', time: '2026-03-20 09:45', confidence: 82, agreement: 68, pnl: -320, accountId: 'sim-compass-swing',
    oneLineReasoning: 'RSI oversold bounce at support',
    context: 'TSLA RSI at 28 with price at major support. Low agreement due to macro uncertainty.',
    deliberation: { claude: ['Oversold bounce setup', 'Support level tested 4 times previously'], gpt: ['RSI divergence suggests reversal', 'Tight stop below support recommended'], gemini: ['Risk elevated due to macro headwinds', 'Position sizing critical'], perplexity: ['Oversold bounces at this level succeed 61%', 'Average bounce magnitude 4.8%'] },
    signals: ['RSI Oversold', 'Support Test', 'Volume Dry-Up', 'Bullish Divergence'],
    outcome: { success: false, pnl: -320, failure: 'Stop hit on gap down after earnings miss' },
    lessons: ['Avoid swing entries ahead of earnings announcements', 'Low agreement scores warrant reduced position size'],
  },
  {
    id: 'T004', asset: 'SPY', action: 'BUY', type: 'ENTRY', time: '2026-04-01 09:31', confidence: 64, agreement: 71, pnl: 96, accountId: 'sim-brain-scalper',
    oneLineReasoning: 'Order flow imbalance detected on open',
    context: 'Large buy imbalance in SPY on market open. VWAP anchored entry.',
    deliberation: { claude: ['Order flow strongly bullish', 'VWAP entry provides good anchor'], gpt: ['Microstructure signals positive', 'Conviction moderate — reduce size'], gemini: ['Scalp setup with tight risk', 'Exit at VWAP reversion target'], perplexity: ['Opening imbalances predict direction 58%', 'Best edge in first 15 minutes'] },
    signals: ['Order Flow Imbalance', 'VWAP Anchor', 'Opening Drive', 'Bid Stacking'],
    outcome: { success: true, pnl: 96, failure: null },
    lessons: ['Opening flow imbalances are reliable for quick scalps', 'Exit discipline at VWAP targets preserves edge'],
  },
  {
    id: 'T005', asset: 'AMD', action: 'SELL', type: 'EXIT', time: '2026-04-05 15:00', confidence: 79, agreement: 85, pnl: 124, accountId: 'sim-compass-swing',
    oneLineReasoning: 'Target hit at resistance zone',
    context: 'AMD reached overhead resistance at $155. Multiple AI models agree on exit.',
    deliberation: { claude: ['Price at resistance with bearish divergence', 'Exit recommended'], gpt: ['Target zone reached', 'Risk-reward no longer favorable for holding'], gemini: ['Resistance confluence with Fibonacci extension', 'Book profits here'], perplexity: ['Historical resistance rejected price 4/5 times', 'Average pullback from this level is 6.2%'] },
    signals: ['Resistance Hit', 'Bearish RSI Divergence', 'Fibonacci Extension', 'Volume Decline'],
    outcome: { success: true, pnl: 124, failure: null },
    lessons: ['High agreement exits tend to be well-timed', 'Taking profits at resistance prevents round-trips'],
  },
  {
    id: 'T006', asset: 'INTC', action: 'BUY', type: 'ENTRY', time: '2026-02-18 11:00', confidence: 91, agreement: 88, pnl: 270, accountId: 'sim-elephant-value',
    oneLineReasoning: 'Deep discount -- P/B below 1.0 with positive FCF',
    context: 'INTC trading below book value with improving margins and government subsidies.',
    deliberation: { claude: ['Extreme value — P/B below 1.0 rare for semis', 'Established company with government backing'], gpt: ['CHIPS Act tailwinds provide catalyst', 'Deep value entry with margin of safety'], gemini: ['Turnaround play with asymmetric risk-reward', 'Downside limited by book value floor'], perplexity: ['P/B sub-1.0 in semis: 76% positive at 12 months', 'Median return 18.4% in similar setups'] },
    signals: ['P/B Below 1.0', 'FCF Positive', 'CHIPS Act Catalyst', 'Margin Improvement'],
    outcome: { success: true, pnl: 270, failure: null },
    lessons: ['Deep value entries in cyclical sectors require patience', 'Government catalysts can accelerate mean reversion'],
  },
  {
    id: 'T007', asset: 'QQQ', action: 'SELL', type: 'EXIT', time: '2026-04-10 13:45', confidence: 58, agreement: 62, pnl: 96, accountId: 'sim-brain-scalper',
    oneLineReasoning: 'VWAP reversion target reached',
    context: 'QQQ reverted to VWAP after morning sell-off. Scalp target hit.',
    deliberation: { claude: ['VWAP reversion complete', 'Minimal edge remaining — exit'], gpt: ['Target reached with low conviction', 'Close for small gain'], gemini: ['Scalp profit secured', 'No reason to hold through afternoon chop'], perplexity: ['VWAP reversion plays have diminishing returns', 'Holding past target reduces Sharpe by 0.3'] },
    signals: ['VWAP Reversion', 'Momentum Fade', 'Spread Compression'],
    outcome: { success: true, pnl: 96, failure: null },
    lessons: ['Scalp discipline means taking small wins consistently', 'Low conviction exits still preserve capital for next setup'],
  },
  {
    id: 'T008', asset: 'GLD', action: 'BUY', type: 'ENTRY', time: '2026-01-20 10:00', confidence: 85, agreement: 90, pnl: 222, accountId: 'sim-sentinel-hedge',
    oneLineReasoning: 'Risk parity rebalance -- hedge allocation increase',
    context: 'Portfolio volatility rising. Gold allocation increased per risk parity model.',
    deliberation: { claude: ['Volatility regime shift detected', 'Gold allocation underweight vs target'], gpt: ['Risk parity model signals rebalance', 'Gold provides correlation benefit'], gemini: ['Cross-asset correlations rising', 'Hedge allocation warranted'], perplexity: ['Gold outperforms in rising correlation environments', 'Average excess return 2.1% per quarter'] },
    signals: ['Volatility Regime Shift', 'Correlation Rising', 'Risk Parity Signal', 'Hedge Underweight'],
    outcome: { success: true, pnl: 222, failure: null },
    lessons: ['Systematic rebalancing outperforms discretionary timing', 'Gold remains effective portfolio hedge in stress regimes'],
  },
  {
    id: 'T009', asset: 'MSFT', action: 'BUY', type: 'ENTRY', time: '2026-03-25 14:00', confidence: 72, agreement: 65, pnl: null, accountId: 'sim-compass-swing',
    oneLineReasoning: 'Bullish engulfing at key support level',
    context: 'MSFT formed bullish engulfing candle at $415 support. Mixed council agreement.',
    deliberation: { claude: ['Pattern valid but macro uncertainty limits conviction', 'Reduce size by 30%'], gpt: ['Bullish engulfing at support is high-probability', 'Moderate size recommended'], gemini: ['Entry valid', 'Stop below engulfing candle low for risk control'], perplexity: ['Bullish engulfing at support succeeds 63%', 'Proper stop placement critical for edge'] },
    signals: ['Bullish Engulfing', 'Support Level', 'Volume Confirmation'],
    outcome: { success: true, pnl: 0, failure: null },
    lessons: ['Pattern entries require volume confirmation for higher probability', 'Mixed agreement should reduce position size, not prevent entry'],
  },
  {
    id: 'T010', asset: 'JPM', action: 'SELL', type: 'EXIT', time: '2026-04-12 11:30', confidence: 83, agreement: 80, pnl: 268, accountId: 'sim-elephant-value',
    oneLineReasoning: 'Approaching fair value -- partial profit taking',
    context: 'JPM near consensus price target. Partial exit to lock in gains.',
    deliberation: { claude: ['Fair value reached', 'Reduce position to manage risk'], gpt: ['Valuation gap closed', 'Partial profit taking prudent here'], gemini: ['Upside limited near fair value', 'Book 50% of position'], perplexity: ['Value positions at fair value should be trimmed', 'Holding past fair value reduces risk-adjusted returns'] },
    signals: ['Fair Value Reached', 'Valuation Gap Closed', 'Momentum Fading', 'Sector Rotation Risk'],
    outcome: { success: true, pnl: 268, failure: null },
    lessons: ['Partial exits at fair value preserve gains and reduce risk', 'Value investing requires disciplined selling, not just buying'],
  },
] as TradeLog[]).map(normalizeTradeLog);

/**
 * @deprecated Use fetchSharedAccounts() instead. This constant is kept
 * only as a fallback while the API is unavailable.
 */
export const SIMULATION_ACCOUNTS: SimulationAccount[] = [];
